// Customer Guide — in-app version of the "Amanar Lead Engine — Customer Guide"
// document. Static content page, same pattern as TermsPage.js. Linked from the
// landing footer and the app header (route: /guide).

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { theme } from './theme';

const S = {
  page: { background: theme.bg, minHeight: '100vh', color: theme.text,
    fontFamily: 'system-ui, -apple-system, sans-serif' },
  wrap: { maxWidth: 820, margin: '0 auto', padding: '32px 22px 80px' },
  brand: { color: theme.primary, fontWeight: 800, letterSpacing: 2, fontSize: '0.85rem', textDecoration: 'none' },
  h1: { color: theme.heading, fontSize: '1.9rem', margin: '10px 0 6px', letterSpacing: '-0.5px' },
  lede: { color: theme.text, fontSize: '1.02rem', lineHeight: 1.6, margin: '0 0 10px' },
  meta: { color: theme.textMute, fontSize: '0.82rem', margin: '0 0 8px' },
  h2: { color: theme.heading, fontSize: '1.2rem', margin: '34px 0 4px', letterSpacing: '-0.3px' },
  sub: { color: theme.textMute, fontSize: '0.9rem', margin: '0 0 12px' },
  h3: { color: theme.heading, fontSize: '1rem', margin: '18px 0 4px' },
  p: { fontSize: '0.95rem', lineHeight: 1.65, margin: '0 0 12px' },
  ul: { margin: '0 0 14px', paddingLeft: 20 },
  li: { fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 8 },
  a: { color: theme.primary },
  tableWrap: { overflowX: 'auto', margin: '0 0 16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', minWidth: 480 },
  th: { textAlign: 'left', color: theme.textMute, fontWeight: 600, fontSize: '0.72rem',
    letterSpacing: '0.06em', textTransform: 'uppercase', padding: '8px 12px',
    borderBottom: `1px solid ${theme.border}` },
  td: { padding: '10px 12px', borderBottom: `1px solid ${theme.border}`, verticalAlign: 'top',
    lineHeight: 1.5, color: theme.text },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem',
    fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap' },
  cta: { display: 'inline-block', marginTop: 10, background: theme.primary, color: '#fff',
    fontWeight: 700, fontSize: '0.9rem', padding: '10px 18px', borderRadius: 8, textDecoration: 'none' },
  footer: { marginTop: 40, paddingTop: 16, borderTop: `1px solid ${theme.border}`,
    color: theme.textMute, fontSize: '0.82rem', lineHeight: 1.5 },
};

const badgeStyle = (bg, fg) => ({ ...S.badge, background: bg, color: fg });

function Table({ head, rows }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead><tr>{head.map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} style={S.td}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GuidePage() {
  useEffect(() => { document.title = 'Customer Guide — Amanar Lead Engine'; }, []);
  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <Link to="/" style={S.brand}>AMANAR LEAD ENGINE</Link>
        <h1 style={S.h1}>Customer Guide</h1>
        <p style={S.meta}>Everything you need to find, work, and export fresh Florida business leads. Ten-minute read.</p>
        <p style={S.lede}>
          Amanar Lead Engine gives you tomorrow&rsquo;s customers today: every new restaurant, bar,
          hotel, and contractor in Florida, pulled from state licensing and corporate records every
          business morning &mdash; often before the business has opened its doors, built a website, or
          been found by your competitors.
        </p>

        <h2 style={S.h2}>Getting started</h2>
        <p style={S.sub}>Three steps to your first lead list</p>
        <ul style={S.ul}>
          <li style={S.li}><b>Create your account.</b> Go to leadengine.amanar.ai, choose <b>Register</b>, and sign in. Free accounts include 50 leads per month &mdash; no credit card required.</li>
          <li style={S.li}><b>Filter to your territory and trade.</b> Pick a category (say, Restaurants &amp; Food Service), then narrow by city or county &mdash; type &ldquo;Tampa&rdquo; or choose Hillsborough. Check <b>Has contact info</b> to see only leads you can reach today.</li>
          <li style={S.li}><b>Take a lead.</b> Click a row to expand it, then take it into your pipeline. It&rsquo;s now in <b>My active</b>, where you can track calls, add notes, and mark outcomes. Other subscribers never see your notes or statuses.</li>
        </ul>

        <h2 style={S.h2}>Reading the table</h2>
        <p style={S.sub}>What the badges mean &mdash; and why they&rsquo;re the whole game</p>
        <p style={S.p}>
          Every lead shows a stage badge telling you how early you are. Earlier is better: a business
          that hasn&rsquo;t opened yet is still choosing its vendors.
        </p>
        <Table
          head={['Badge', 'What it means', 'How to use it']}
          rows={[
            [<span style={badgeStyle('#0E3A2E', theme.accent)}>INBOUND</span>,
              'The owner submitted or claimed this listing themselves and agreed to be contacted by vendors.',
              'Call these first. Contact info is verified by the owner, and they’re expecting outreach.'],
            [<span style={badgeStyle('#1E3A5F', '#60A5FA')}>PRE-OPENING</span>,
              'A facility in build-out — plan review filed with the state, doors not open yet.',
              'The golden window. They’re actively buying: equipment, POS, insurance, suppliers, marketing.'],
            [<span style={badgeStyle('#3A2E14', '#FBBF24')}>NEW OWNER</span>,
              'An existing business just changed hands.',
              'New owners re-evaluate every vendor relationship. Treat like a new business with existing revenue.'],
            [<span style={badgeStyle(theme.surface, theme.text)}>NEW LICENSE</span>,
              'A newly issued state license (restaurant, liquor, contractor, etc.).',
              'Fresh business, recently operational. Great timing for anything they haven’t set up yet.'],
          ]}
        />
        <h3 style={S.h3}>The columns</h3>
        <p style={S.p}>
          <b>Business</b> and <b>Category</b> identify the lead. <b>City / County</b> come from state
          records. <b>Phone / Website / Email</b> are filled three ways: directly from state filings,
          from the owner (Inbound), or found by our AI research layer &mdash; which works through new
          leads continuously, so a lead that&rsquo;s blank today often has contact info tomorrow.
          <b> Contact</b> shows the registered agent (often the owner or their attorney) from corporate
          filings. <b>Filing date</b> is when the business registered with the state.
        </p>

        <h2 style={S.h2}>Finding leads</h2>
        <p style={S.sub}>Filters and search</p>
        <Table
          head={['I want to…', 'Do this']}
          rows={[
            ['Only my industry', 'Use the Category dropdown — Restaurants, Bars & Liquor Licenses, Hotels & Lodging, General Contractors, HVAC, Roofing, Plumbing, Electrical, Pool & Spa.'],
            ['Only my city', 'Type in the City box (partial names work — “st pete” finds St Petersburg). Searches the entire database, not just what’s on screen.'],
            ['Only my county', 'Pick from the County dropdown (all 67 Florida counties).'],
            ['Only callable leads', 'Check “Has contact info” — hides anything without a phone, email, or website.'],
            ['Find one business', 'Type any part of the name in “Search business name.”'],
            ['Freshest first', 'Sort “Newest first” (default), or “Enriched first” to put leads with the most contact info on top.'],
          ]}
        />

        <h2 style={S.h2}>Working leads</h2>
        <p style={S.sub}>Your private pipeline</p>
        <p style={S.p}>
          The lead pool is shared &mdash; every subscriber sees the same businesses. Your work on them
          is private. When you take a lead, it counts against your monthly quota and gets a status only
          you can see: New, Contacted, Qualified, Won, Lost, or Not a fit.
        </p>
        <p style={S.p}>
          Expand any lead to change its status or add notes (&ldquo;Spoke to owner Maria, opening
          mid-August, needs POS quote by Friday&rdquo;). Use the top filter tabs &mdash; Available,
          My active, Contacted, Won &mdash; to manage your day.
        </p>
        <p style={S.p}><b>Tip:</b> &ldquo;Not a fit&rdquo; removes a lead from your active view without wasting it &mdash; and helps you remember you already looked.</p>

        <h2 style={S.h2}>Exporting</h2>
        <p style={S.sub}>Take your leads with you</p>
        <p style={S.p}>
          <b>Export CSV</b> downloads exactly what you&rsquo;ve filtered on screen &mdash; ready for
          Excel or any CRM. For phone outreach, the <b>dialer exports</b> format your assigned leads for
          autodialer software: a generic CSV, a VanDyke-format file, or a plain phone list.
        </p>

        <h2 style={S.h2}>Plans</h2>
        <p style={S.sub}>Quotas and upgrading</p>
        <Table
          head={['Plan', 'Leads / month', 'Best for']}
          rows={[
            ['Free', '50', 'Trying the product in one territory'],
            ['Pro', '500', 'A full-time rep or small team'],
            ['Enterprise', '5,000', 'Teams working multiple categories statewide'],
          ]}
        />
        <p style={S.p}>
          Quotas reset monthly and count only leads you take &mdash; browsing, filtering, and viewing
          are always unlimited. Upgrade any time from the billing menu; changes take effect immediately.
        </p>

        <h2 style={S.h2}>FAQ</h2>
        <h3 style={S.h3}>Where does this data come from?</h3>
        <p style={S.p}>Florida state public records: DBPR licensing feeds (restaurant plan reviews, new food-service and lodging licenses, ownership changes, liquor licenses, contractor and electrical boards) cross-referenced with Division of Corporations (Sunbiz) filings. Inbound leads come from business owners who submit their own information.</p>
        <h3 style={S.h3}>How fresh is it?</h3>
        <p style={S.p}>The database updates every business morning by about 9:15 AM Eastern. A restaurant that filed its plan review yesterday is typically in your results today.</p>
        <h3 style={S.h3}>Is it legal to contact these businesses?</h3>
        <p style={S.p}>The listings are compiled from public government records, and business-to-business outreach to them is standard practice. Inbound-badged leads go further &mdash; the owner explicitly agreed to vendor contact. As always, follow do-not-call and consent rules that apply to your outreach method and industry; that responsibility stays with you.</p>
        <h3 style={S.h3}>Why do some leads have no phone or email?</h3>
        <p style={S.p}>Brand-new businesses often don&rsquo;t have a public footprint yet. Our AI research layer keeps working through them daily &mdash; and automatically retries businesses 30&ndash;90 days after registration, when most have built a web presence. Check back, or sort by &ldquo;Enriched first.&rdquo;</p>
        <h3 style={S.h3}>Can other subscribers see my notes and statuses?</h3>
        <p style={S.p}>No. The business listings are shared; everything you do with them &mdash; taken leads, statuses, notes &mdash; is visible only to your account.</p>
        <h3 style={S.h3}>What geography do you cover?</h3>
        <p style={S.p}>All of Florida, all 67 counties. Additional states are on the roadmap.</p>
        <h3 style={S.h3}>A lead&rsquo;s info is wrong &mdash; what do I do?</h3>
        <p style={S.p}>State records occasionally lag or contain errors. The AI layer corrects cities and contact details over time, and owners can fix their own listing via the claim flow. If something looks badly off, flag it to support and move on.</p>
        <h3 style={S.h3}>What happens when I hit my monthly quota?</h3>
        <p style={S.p}>You can keep browsing and working leads you&rsquo;ve already taken, but taking new ones requires an upgrade or waiting for the monthly reset.</p>

        <h2 style={S.h2}>Quick reference</h2>
        <Table
          head={['Task', 'Where']}
          rows={[
            ['Sign in / register', 'leadengine.amanar.ai'],
            ['Filter by trade', 'Category dropdown, top bar'],
            ['Filter by geography', 'City box + County dropdown, above the table'],
            ['Callable leads only', '“Has contact info” checkbox'],
            ['Best leads first', 'Look for INBOUND and PRE-OPENING badges'],
            ['Claim a lead', 'Click the row → take it into your pipeline'],
            ['Log a call', 'Expand the lead → set status → add a note'],
            ['Export for CRM', 'Export CSV (respects your current filters)'],
            ['Export for dialer', 'Dialer export menu (generic CSV / VanDyke / phone list)'],
            ['Upgrade plan', 'Billing menu → choose Pro or Enterprise'],
          ]}
        />

        <Link to="/app" style={S.cta}>Open the app &rarr;</Link>

        <p style={S.footer}>
          Amanar Lead Engine &middot; Data compiled from Florida DBPR and Division of Corporations
          public records, refreshed every business day. Questions? Contact your Amanar representative.
        </p>
      </div>
    </div>
  );
}
